import { detectNamespace } from "../../shared/caip2";
import { getLogger } from "../../shared/logger";
import { ChainService } from "../chains/chain-service";
import { SolanaOperationsEnum, StakewiseOperationsEnum } from "../security/types";
import stakingProtocolsData from "../../data/staking_protocols.json";
import { StakingProtocolFactory } from "./factory/staking-factory";
import { ProtocolSelectionStrategyManager } from "./protocols/protocol-selection/protocol-selection-manager";
/**
 * Main module for staking operations across multiple protocols (StakeWise, Solana Native Staking).
 *
 * @remarks
 * The StakingModule provides comprehensive staking functionality including:
 * - **Multi-Protocol Staking**: Stake assets via StakeWise (liquid) or Solana Native Staking
 * - **Staking Metadata**: Retrieve normalised APY, fees, and protocol-specific details via `getStakingMetadata`
 * - **Transaction Building**: Create staking, unstaking, and withdrawal transactions
 * - **Analytics**: Retrieve staking rewards, history, and performance data
 * - **Gas Optimization**: Estimate gas costs for all staking operations
 *
 * **Staking Flow Overview:**
 * 1. **Query**: Retrieve staking metadata (APY, fees, capacity) via `getStakingMetadata`
 * 2. **Deposit**: Stake assets into the target protocol
 * 3. **Earn**: Accumulate staking rewards automatically through validator operations
 * 4. **Monitor**: Track performance, rewards, and protocol health metrics
 * 5. **Unstake**: Request withdrawal of staked assets (subject to protocol delays)
 * 6. **Withdraw**: Claim unstaked assets once the withdrawal period completes
 * @example
 * **Basic Staking Operation**
 * ```typescript
 * import { StakingModule } from "@blockdaemon/bd-sdk";
 *
 * const staking = new StakingModule({
 *   bdApiKey: "your-api-key",
 * });
 *
 * // Get staking metadata
 * const info = await staking.getStakingMetadata({ chainId: "eip155:1" });
 * console.log(`APY: ${info.apyPercentage}%`);
 *
 * // Build staking transaction
 * const stakeTx = await staking.buildStakeTx({
 *   assets: BigInt("1000000000000000000"), // 1 ETH in wei
 *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   chainId: "eip155:1"
 * });
 * ```
 *
 * @example
 * **Using Mainnet Vault**
 * ```typescript
 * import { StakingModule, BDVaults } from "@blockdaemon/bd-sdk";
 *
 * const staking = new StakingModule({
 *   bdApiKey: "your-api-key"
 * });
 *
 * // Build staking transaction for Ethereum mainnet
 * const stakeTx = await staking.buildStakeTx({
 *   assets: BigInt("1000000000000000000"), // 1 ETH in wei
 *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   vaultAddress: BDVaults.ethereum, // or BDVaults.default
 *   chainId: "eip155:1"
 * });
 * ```
 *
 * @example
 * **Complete Staking Workflow**
 * ```typescript
 * // 1. Check staking metadata and APY
 * const info = await staking.getStakingMetadata({ chainId: "eip155:1" });
 * console.log(`Current APY: ${info.apyPercentage}%`);
 * if (info.detail.type === "stakewise") {
 *   console.log(`Available capacity: ${info.detail.vault.capacity} ETH`);
 *
 *   // 2. Estimate gas costs
 *   const gasEstimate = await staking.estimateDepositGas({
 *     assets: BigInt("1000000000000000000"),
 *     userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *     chainId: "eip155:1"
 *   });
 *   console.log(`Estimated gas: ${gasEstimate.toString()}`);
 *
 *   // 3. Build and execute transaction
 *   const stakeTx = await staking.buildStakeTx({
 *     assets: BigInt("1000000000000000000"),
 *     userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *     chainId: "eip155:1"
 *   });
 *
 *   // Transaction ready for signing and broadcast
 *   console.log("Transaction data:", stakeTx.data);
 * }
 * ```
 *
 * @example
 * **Monitoring Staking Performance**
 * ```typescript
 * // Get comprehensive staking analytics
 * const info = await staking.getStakingMetadata({ chainId: "eip155:1" });
 * console.log(`APY: ${info.apyPercentage}%`);
 * console.log(`Fee: ${info.feePercentage}%`);
 * if (info.detail.type === "stakewise") {
 *   console.log(`Total Assets: ${info.detail.vault.totalAssets}`);
 *   console.log(`Performance: ${info.detail.vault.performance}%`);
 * }
 * ```
 *
 * @see {@link https://docs.stakewise.io/} StakeWise Protocol Documentation
 * @see {@link IStakingModuleOptions} for configuration options
 *
 * @remarks
 * The StakingModule bootstraps protocol instances through `StakingProtocolFactory`, then
 * indexes them into maps by protocol id and by chain. Protocol selection at request time is
 * delegated to `ProtocolSelectionStrategyManager` (defaulting to `ChainStrategy`), which
 * reads those maps to choose the appropriate protocol based on the provided context (e.g., chainId).
 * This separation of concerns allows adding new protocols via the factory while evolving
 * selection logic independently through strategies.
 *
 * @public
 */
export class StakingModule {
    bdApiKey;
    logger;
    security;
    options;
    chainService;
    supportedStakingProtocols = [];
    protocolFactory;
    protocolSelectionManager;
    stakingProtocolsByChain = new Map();
    stakingProtocolsById = new Map();
    protocolConfig;
    /**
     * Creates a new StakingModule instance with the specified configuration.
     *
     * @param options - Configuration options for the staking module
     * @throws {Error} When the BD API key is missing or empty
     *
     * @example
     * ```typescript
     * const staking = new StakingModule({
     *   bdApiKey: process.env.BD_API_KEY,
     *   logger: customLogger // optional
     * });
     * ```
     */
    constructor(options) {
        this.logger = options.logger || getLogger({ level: "info", label: "bd-sdk-staking-module" });
        if (typeof options.bdApiKey === "string" && options.bdApiKey.trim() === "") {
            throw new Error("BD API key is required");
        }
        if (!options.bdApiKey) {
            this.logger.warn("BD API key is not set. Some staking operations may fail with 401 Unauthorized.");
            this.bdApiKey = undefined;
        }
        else {
            this.bdApiKey = options.bdApiKey;
        }
        this.options = options;
        this.security = options.security;
        // Use rpcBasePath if provided, otherwise fall back to basePath
        const effectiveRpcBasePath = options.rpcBasePath || options.basePath;
        const chainServiceOptions = {
            bdApiKey: options.bdApiKey || "",
            logger: this.logger,
            rpcBasePath: effectiveRpcBasePath,
        };
        this.chainService = new ChainService(chainServiceOptions);
        this.protocolFactory = StakingProtocolFactory.getInstance(this.logger);
        try {
            const stakingData = stakingProtocolsData;
            this.supportedStakingProtocols = stakingData;
            this.logger.debug("Supported staking protocols loaded successfully", {
                supportedStakingProtocols: this.supportedStakingProtocols,
            });
        }
        catch (error) {
            this.logger.error("Error loading supported staking protocols", { error });
            throw error;
        }
        // protocol configuration
        this.protocolConfig = options.protocolConfig;
        this.initializeStakingProtocols();
        this.initializeProtocolSelectionManager();
    }
    /**
     * Returns the security module if available, otherwise undefined.
     * Used internally to conditionally verify transactions.
     */
    getSecurityModule() {
        return this.security;
    }
    initializeStakingProtocols() {
        for (const stakingProtocol of this.supportedStakingProtocols) {
            try {
                // Use factory to create protocol instance
                const protocolInstance = this.protocolFactory.create({
                    logger: this.logger,
                    name: stakingProtocol.name,
                    description: stakingProtocol.description,
                    url: stakingProtocol.url,
                    logoURI: stakingProtocol.logoURI,
                    availableChains: stakingProtocol.availableChains,
                    stakingProtocolType: stakingProtocol.stakingProtocolType,
                    chainService: this.chainService,
                    referralTag: this.options.referralTag,
                    bdApiKey: this.bdApiKey,
                    basePath: this.options.basePath,
                    protocolConfig: this.protocolConfig,
                });
                // Store protocol by its generated id
                this.stakingProtocolsById.set(protocolInstance.getID(), protocolInstance);
                // Map each chain to this protocol instance
                for (const chainId of stakingProtocol.availableChains) {
                    this.stakingProtocolsByChain.set(chainId, protocolInstance);
                    this.logger.debug(`Mapped chain ${chainId} to protocol ${stakingProtocol.name} (id: ${protocolInstance.getID()})`);
                }
                this.logger.info(`Successfully initialized protocol: ${stakingProtocol.name}`, {
                    type: stakingProtocol.stakingProtocolType,
                    chains: stakingProtocol.availableChains.length,
                    protocolId: protocolInstance.getID(),
                });
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                this.logger.warn(`Failed to initialize staking protocol "${stakingProtocol.name}": ${errorMessage}`);
                this.logger.debug(`Debug info for ${stakingProtocol.name} initialization:`, { error });
            }
        }
        const initializedCount = this.stakingProtocolsById.size;
        const totalCount = this.supportedStakingProtocols.length;
        this.logger.info("Protocol initialization complete");
        this.logger.info(`Initialized ${initializedCount} protocols out of ${totalCount}`);
        this.logger.info(`Supported chains: ${Array.from(this.stakingProtocolsByChain.keys())}`);
        if (initializedCount < totalCount) {
            this.logger.warn(`Only ${initializedCount} of ${totalCount} staking protocols initialized successfully.
          Requests for uninitialized protocols will fail at runtime.`);
        }
        if (initializedCount === 0) {
            this.logger.warn("No staking protocols were successfully initialized; runtime selection may fail for requested chains.");
        }
    }
    initializeProtocolSelectionManager() {
        this.protocolSelectionManager = new ProtocolSelectionStrategyManager({
            logger: this.logger,
            stakingProtocolsByChain: this.stakingProtocolsByChain,
            stakingProtocolsById: this.stakingProtocolsById,
            supportedStakingProtocols: this.supportedStakingProtocols,
        });
    }
    /**
     * Retrieves information about the staking metadata (validator or vault) for
     * the given chain, including normalised APY and fee data.
     *
     * @remarks
     * Unified replacement for the former `getVault` (StakeWise) and
     * `getStakingMetadata` (Solana) methods, now removed. The response carries normalised
     * `apyPercentage` and `feePercentage` at the top level plus a discriminated
     * `detail` union (`detail.type === "stakewise"` or `"solana"`) for full
     * protocol-specific data.
     *
     * @param request - Operator request with chainId, optional operatorAddress, and options
     * @returns Promise resolving to operator information with normalised metrics
     * @throws {Error} When no protocol is available for the specified chain
     *
     * @example
     * ```typescript
     * // StakeWise
     * const info = await staking.getStakingMetadata({ chainId: "eip155:1" });
     * console.log(`APY: ${info.apyPercentage}%`);
     *
     * // Solana
     * const info = await staking.getStakingMetadata({
     *   chainId: "solana:mainnet",
     *   operatorAddress: "FQwewNXahV7MiZcLpY6p1xhUs2acVGQ3U5Xxc7FzV571",
     * });
     * ```
     */
    async getStakingMetadata(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Getting staking metadata using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.getStakingMetadata(request);
    }
    /**
     * Builds a staking transaction for depositing ETH into the vault.
     *
     * @param request - Staking transaction request parameters
     * @returns Promise resolving to transaction data ready for signing
     * @throws {Error} When no protocol is available for the specified chain
     */
    async buildStakeTx(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Building stake transaction using protocol ${protocol.getName()} for chain ${request.chainId}`);
        const response = await protocol.buildStakeTx(request);
        const chainId = request.chainId;
        const namespace = detectNamespace(chainId);
        const operation = namespace === "solana" ? SolanaOperationsEnum.STAKE : StakewiseOperationsEnum.STAKE;
        this.getSecurityModule()?.verifyIntegrity({
            operation,
            request,
            encodedTx: response,
        });
        return response;
    }
    /**
     * Estimates gas cost for a staking deposit transaction.
     *
     * @param request - Staking transaction request parameters for gas estimation
     * @returns Promise resolving to estimated gas amount in gas units
     * @throws {Error} When no protocol is available for the specified chain
     */
    async estimateDepositGas(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Estimating deposit gas using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.estimateDepositGas(request);
    }
    /**
     * Builds a withdrawal transaction for claiming unstaked assets.
     *
     * @param request - Withdrawal request parameters
     * @returns Promise resolving to withdrawal transaction data ready for signing
     * @throws {Error} When no protocol is available for the specified chain
     */
    async buildWithdrawTx(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Building withdraw transaction using protocol ${protocol.getName()} for chain ${request.chainId}`);
        const response = await protocol.buildWithdrawTx(request);
        const chainId = request.chainId;
        const namespace = detectNamespace(chainId);
        const operation = namespace === "solana" ? SolanaOperationsEnum.WITHDRAW : StakewiseOperationsEnum.WITHDRAW;
        this.getSecurityModule()?.verifyIntegrity({
            operation,
            request,
            encodedTx: response,
        });
        return response;
    }
    /**
     * Retrieves the maximum withdrawable amount for a user.
     *
     * @param request - Maximum withdrawal request parameters
     * @returns Promise resolving to the maximum withdrawable amount in wei
     * @throws {Error} When no protocol is available for the specified chain
     */
    async getMaxWithdraw(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Getting max withdraw using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.getMaxWithdraw(request);
    }
    /**
     * Previews StakeWise vault shares for a native asset amount (on-chain `convertToShares`).
     *
     * @param request - Asset amount in wei, optional vault address, and chain id
     * @returns Share amount with the same rounding as the vault contract
     * @throws {Error} When the chain uses a protocol that does not support this call (e.g. Solana)
     */
    async convertVaultAssetsToShares(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`convertVaultAssetsToShares using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.convertVaultAssetsToShares(request);
    }
    /**
     * Retrieves exit queue positions for tracking withdrawal progress.
     *
     * @param request - Exit queue positions request parameters
     * @returns Promise resolving to exit queue positions information
     * @throws {Error} When no protocol is available for the specified chain
     */
    async getExitQueuePositions(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Getting exit queue positions using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.getExitQueuePositions(request);
    }
    /**
     * Builds a claim exit queue transaction for claiming assets from completed withdrawals.
     *
     * @param request - Exit queue claim request parameters
     * @returns Promise resolving to claim transaction data ready for signing
     * @throws {Error} When no protocol is available for the specified chain
     */
    async claimExitQueueTx(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Building claim exit queue transaction using protocol ${protocol.getName()} for chain ${request.chainId}`);
        const response = await protocol.claimExitQueueTx(request);
        this.getSecurityModule()?.verifyIntegrity({
            operation: StakewiseOperationsEnum.CLAIM_EXIT_QUEUE,
            request,
            encodedTx: response,
        });
        return response;
    }
    /**
     * Retrieves the current stake balance for a user.
     *
     * @param request - Stake balance request parameters
     * @returns Promise resolving to current stake balance information
     * @throws {Error} When no protocol is available for the specified chain
     */
    async getStakeBalance(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Getting stake balance using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.getStakeBalance(request);
    }
    /**
     * Gets a single Solana stake account by its public key address.
     *
     * @remarks
     * This method is only supported for Solana chains (`solana:mainnet`, `solana:testnet`).
     * Useful when you already hold a stake account address from a prior {@link getStakeBalance}
     * call and want to refresh a single account rather than refetching all accounts for the
     * delegator.
     *
     * @param request - Request containing `stakeAccountAddress` and `chainId`
     * @returns Promise resolving to the stake account details
     * @throws {Error} When the chainId is not a Solana chain
     * @throws {Error} When the stakeAccountAddress is not a valid Solana address
     * @throws {Error} When no stake account is found for the given address
     *
     * @example
     * ```typescript
     * const result = await staking.getStakeAccount({
     *   stakeAccountAddress: "6rHbL9Q4...",
     *   chainId: "solana:mainnet",
     * });
     *
     * console.log(`Staked: ${result.account.stakedAmount} lamports`);
     * console.log(`Withdrawable: ${result.account.isWithdrawable}`);
     * ```
     */
    async getStakeAccount(request) {
        const chainId = request.chainId;
        const namespace = detectNamespace(chainId);
        if (namespace !== "solana") {
            throw new Error(`getStakeAccount is only supported for Solana chains. Received chain: ${chainId}`);
        }
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Getting stake account using protocol ${protocol.getName()} for chain ${chainId}`);
        return protocol.getStakeAccount(request);
    }
    /**
     * Retrieves staker actions and transaction history for a user.
     *
     * @param request - Staker actions request parameters
     * @returns Promise resolving to array of staker actions
     * @throws {Error} When no protocol is available for the specified chain
     */
    async getStakerActions(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Getting staker actions using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.getStakerActions(request);
    }
    /**
     * Retrieves user rewards history over a specified time period.
     *
     * @param request - User rewards request parameters
     * @returns Promise resolving to array of daily rewards data
     * @throws {Error} When no protocol is available for the specified chain
     */
    async getUserRewards(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Getting user rewards using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.getUserRewards(request);
    }
    /**
     * Claims accumulated rewards for a user.
     *
     * @param request - Claim rewards request parameters
     * @returns Promise resolving to claim transaction response
     * @throws {Error} When no protocol is available for the specified chain
     */
    async claimRewards(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Claiming rewards using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.claimRewards(request);
    }
    /**
     * Estimates gas cost for claiming rewards.
     *
     * @param request - Claim rewards request parameters for gas estimation
     * @returns Promise resolving to estimated gas amount in gas units
     * @throws {Error} When no protocol is available for the specified chain
     */
    async estimateClaimRewardsGas(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Estimating claim rewards gas using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.estimateClaimRewardsGas(request);
    }
    /**
     * High-level unstake operation that handles the complete unstaking flow.
     *
     * @param request - Unstake request parameters
     * @returns Promise resolving to unstake result with transaction details and status
     * @throws {Error} When no protocol is available for the specified chain
     */
    async unstake(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Unstaking using protocol ${protocol.getName()} for chain ${request.chainId}`);
        return protocol.unstake(request);
    }
    /**
     * Prepares a stake position for withdrawal by deactivating the stake.
     *
     * @remarks
     * For Solana native staking, this deactivates the stake delegation. After deactivation
     * and a cooldown period (approximately 2-3 days), the stake can be withdrawn.
     *
     * @param request - Prepare to withdraw request parameters
     * @returns Promise resolving to transaction data ready for signing
     * @throws {Error} When no protocol is available for the specified chain
     *
     * @example
     * ```typescript
     * const result = await staking.prepareToWithdraw({
     *   userAddress: "DelegatorPubkey...",
     *   stakeAccountAddress: "StakeAccountPubkey...",
     *   chainId: "solana:mainnet"
     * });
     * ```
     */
    async prepareToWithdraw(request) {
        const protocol = this.protocolSelectionManager.select(request);
        this.logger.debug(`Preparing to withdraw using protocol ${protocol.getName()} for chain ${request.chainId}`);
        const response = await protocol.prepareToWithdraw(request);
        const chainId = request.chainId;
        const namespace = detectNamespace(chainId);
        if (namespace === "solana") {
            // Build verification request with all fields that should be verified
            const verificationRequest = {
                chainId,
                stakeAccountPubkey: request.stakeAccountAddress || "",
                payerPubkey: request.userAddress,
                durableNonce: request.durableNonce,
            };
            this.getSecurityModule()?.verifyIntegrity({
                operation: SolanaOperationsEnum.DEACTIVATE,
                request: verificationRequest,
                encodedTx: response,
            });
        }
        return response;
    }
    /**
     * Gets the protocol instance for a specific chain.
     *
     * @param chainId - The chain identifier (e.g., "eip155:1")
     * @returns The protocol instance or undefined if not supported
     */
    getProtocolsForChain(chainId) {
        return this.protocolSelectionManager.getProtocolsForChain(chainId);
    }
    /**
     * Gets the protocol instance by its generated id.
     *
     * @param protocolId - The dynamically generated protocol id
     * @returns The protocol instance or undefined if not found
     */
    getProtocolById(protocolId) {
        return this.protocolSelectionManager.getProtocolById(protocolId);
    }
    /**
     * Lists all supported chains and their associated protocols.
     *
     * @returns Array of supported chain IDs
     */
    getSupportedChains() {
        return this.protocolSelectionManager.getSupportedChains();
    }
    /**
     * Gets the current protocol selection strategy.
     *
     * @returns The current protocol selection strategy
     */
    getProtocolSelectionStrategy() {
        return this.protocolSelectionManager.getStrategy();
    }
    /**
     * Sets the protocol selection strategy.
     *
     * @param strategy - The protocol selection strategy to use
     */
    setProtocolSelectionStrategy(strategy) {
        return this.protocolSelectionManager.setStrategy(strategy);
    }
    /**
     * Lists all loaded protocol instances with their metadata.
     *
     * @returns Array of protocol information
     */
    getLoadedProtocols() {
        const protocols = Array.from(this.stakingProtocolsById.values());
        return protocols.map((protocol) => ({
            id: protocol.getID(),
            name: protocol.getName(),
            type: protocol.stakingProtocolType,
            chains: protocol.availableChains,
        }));
    }
}
//# sourceMappingURL=data:application/json;base64,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